import { AxiosInstance } from 'axios';
import { Event, ListEventsParams } from '../types/events';
import { Tag } from '../types/tags';
import { buildOffsetParams } from '../util/pagination';
import { EventSchema, ListEventsParamsSchema } from '../z/events';
import { TagSchema } from '../z/tags';

export class EventsClient {
  constructor(private readonly http: AxiosInstance) {}

  /**
   * List all events with optional filtering and pagination.
   *
   * This endpoint returns a paginated list of events with optional filtering
   * by active status, category, tags, and date ranges. Useful for building
   * event browsing interfaces or finding events by specific criteria.
   *
   * @example
   * ```typescript
   * // Get active politics events
   * const events = await client.events.list({
   *   active: true,
   *   category: 'politics',
   *   limit: 50
   * });
   *
   * // Get events within a date range
   * const upcomingEvents = await client.events.list({
   *   startDate: '2024-01-01',
   *   endDate: '2024-12-31',
   *   active: true
   * });
   * ```
   *
   * @param params - Optional query parameters for filtering and pagination
   * @param params.limit - Number of events to return (default: 10)
   * @param params.offset - Number of events to skip (default: 0)
   * @param params.active - Filter by active status (true/false)
   * @param params.category - Filter by category
   * @param params.tags - Filter by tags (array of tag IDs or slugs)
   * @param params.startDate - Filter by start date (ISO 8601 format)
   * @param params.endDate - Filter by end date (ISO 8601 format)
   *
   * @returns Promise<Event[]> - Array of events
   * @throws {Error} When the API request fails or returns invalid data
   *
   * @see {@link https://docs.polymarket.com/api-reference/events/list-events} - API Documentation
   */
  async list(params?: ListEventsParams): Promise<Event[]> {
    const validatedParams = params ? ListEventsParamsSchema.parse(params) : {};
    const queryParams = buildOffsetParams(validatedParams);
    try {
      const response = await this.http.get('/events', { params: queryParams });
      const validatedData = EventSchema.array().parse(response.data);

      return validatedData as Event[];
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to list events: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Get an event by its unique identifier.
   *
   * This endpoint returns detailed information about a specific event,
   * including its description, dates, status, and associated markets.
   *
   * @example
   * ```typescript
   * const event = await client.events.getById('123');
   * console.log('Event Title:', event.title);
   * console.log('Status:', event.status);
   * console.log('Start Date:', event.startDate);
   * console.log('Markets:', event.markets.length);
   * ```
   *
   * @param id - Event ID
   * @returns Promise<Event> - Event data with markets and metadata
   * @throws {Error} When the event is not found or the API request fails
   *
   * @see {@link https://docs.polymarket.com/api-reference/events/get-event-by-id} - API Documentation
   */
  async getById(id: string): Promise<Event> {
    try {
      const response = await this.http.get(`/events/${id}`);
      const validatedData = EventSchema.parse(response.data);

      return validatedData as Event;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to get event by ID ${id}: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Get tags associated with a specific event.
   *
   * This endpoint returns all tags that are associated with the specified event,
   * useful for understanding event categorization and finding related content.
   *
   * @example
   * ```typescript
   * const tags = await client.events.getTags('123');
   * console.log('Event Tags:', tags.map(tag => tag.name));
   *
   * // Find politics-related tags
   * const politicsTags = tags.filter(tag => tag.category === 'politics');
   * ```
   *
   * @param eventId - Event ID
   * @returns Promise<Tag[]> - Array of tags associated with the event
   * @throws {Error} When the event is not found or the API request fails
   *
   * @see {@link https://docs.polymarket.com/api-reference/events/get-event-tags} - API Documentation
   */
  async getTags(eventId: string): Promise<Tag[]> {
    try {
      const response = await this.http.get(`/events/${eventId}/tags`);
      const validatedData = TagSchema.array().parse(response.data);

      return validatedData;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to get tags for event ${eventId}: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Get an event by its human-readable slug.
   *
   * This endpoint returns detailed information about an event using its
   * slug instead of the numeric ID. Slugs are more user-friendly and
   * stable identifiers.
   *
   * @example
   * ```typescript
   * const event = await client.events.getBySlug('2024-presidential-election');
   * console.log('Event:', event.title);
   * console.log('Slug:', event.slug);
   * console.log('Status:', event.status);
   * ```
   *
   * @param slug - Event slug (human-readable identifier)
   * @returns Promise<Event> - Event data with markets and metadata
   * @throws {Error} When the event is not found or the API request fails
   *
   * @see {@link https://docs.polymarket.com/api-reference/events/get-event-by-slug} - API Documentation
   */
  async getBySlug(slug: string): Promise<Event> {
    try {
      const response = await this.http.get(`/events/slug/${slug}`);
      const validatedData = EventSchema.parse(response.data);

      return validatedData as Event;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to get event by slug ${slug}: ${error.message}`);
      }
      throw error;
    }
  }
}
